import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AsteriskIcon,
  FileTextIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";

import { asMutation, initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { List, ListItem } from "simplydo/react/ui/list";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import utils from "utils/utils";

import { useChildIdea } from "components/ideas/hooks/useIdea";
import type {
  CreditReportResponse,
  IdeaWithBusinessProfileDetails,
} from "components/ideas/types";
import { UserChip } from "components/lib/Chips";
import IdeaBusinessProfileModal from "components/lib/IdeaBusinessProfileModal/IdeaBusinessProfileModal";
import {
  AddressLayout,
  ContactLayout,
} from "components/lib/IdeaBusinessProfileModal/Tabs/Contact";
import { Overview } from "components/lib/IdeaBusinessProfileModal/Tabs/Overview";
import { getCreditsafeCompanyId } from "components/lib/IdeaBusinessProfileModal/Tabs/identifierLookup";

import { BusinessProfileSearcher } from "./BusinessProfileSearcher";
import BusinessProfileSelector from "./ProfileSelector";

const IdeaSection = styled.div`
  > h5 {
    display: block;
    margin: 0;
  }
  > .title-explainer {
    color: gray;
    display: block;
    margin: 2.5px 0 20px;
  }
`;

// `updateIdea` can be undefined if the idea is open in an idea preview, in those cases the idea is never editable and no update function is available. However, this is not an issue as the component doesn't expose any editing functionality in that case anyway
type IIdeaBusinessProfileComponent = {
  isEditing?: boolean;
  style?: object;
  updateIdea: (
    updatedValue: any,
    path: string[],
    suppressSave?: boolean,
  ) => void;
  setFieldRef?: (ref: HTMLDivElement | null, key: string) => void;
};

const IdeaBusinessProfileComponent = ({
  isEditing,
  style,
  updateIdea,
  setFieldRef,
}: IIdeaBusinessProfileComponent) => {
  const [idea] = useChildIdea<unknown, IdeaWithBusinessProfileDetails>();

  // Track state of the profile editor - what stage the user is at
  const [creditsafeEnabled, setCreditsafeEnabled] = useState(false);
  const theme = useTheme();
  const profilesQuery = useQuery(initUseQuery(api.users.getProfiles, []));

  const [generatingCreditReport, setGeneratingCreditReport] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    openInEditMode: false,
    defaultTab: 0,
  });

  const ideaBusinessProfile = idea.lockedOwnerIdeaBusinessProfile;
  const ideaBusinessProfileDetails = idea.ideaBusinessProfileDetails;
  const {
    name,
    address = {},
    addresses,
    creditsafeRegNo,
    foundingYear,
    vatNo,
    phoneNumber,
    contacts,
    websiteUrl,
    websiteUrlSkipped,
    type,
    status,
    size,
    vatNoSkipped,
    creditsafeRegNoSkipped,
    creditsafeReportUrl,
  } = ideaBusinessProfile || {};
  const creditsafeCompanyId = getCreditsafeCompanyId(ideaBusinessProfile);

  const { country, city, streetAddress, postcode, region } = address || {};

  const primaryContact = contacts?.find(
    (contact) => contact.id === ideaBusinessProfileDetails?.primaryContact,
  );
  const tradingAddress = ideaBusinessProfileDetails?.tradingAddress
    ? [{ ...address, id: "registeredAddress" }, ...(addresses || [])]?.find(
        (address) => address.id === ideaBusinessProfileDetails?.tradingAddress,
      )
    : null;
  const reloadUser = useUserReload();
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const challengeId = idea?.challenge;
  const hasReportPermissions =
    utils.hasPermission(user, "challenge.viewIdeas", idea.challenge) ||
    utils.canManageChallenge(user, idea.ownerChallenge);
  const canEditProfile = utils.hasPermission(
    user,
    "ideaBusinessProfile.editDetails",
    ideaBusinessProfile?._id,
  );

  const onProfileUpdated = (
    businessProfile: OpenAPI.Schemas["IdeaBusinessProfile"],
  ) => {
    api.setQueryData(api.users.getProfiles, [], (prev) => ({
      ...(prev ?? { profiles: [], joinableProfiles: [] }),
      profiles: (prev?.profiles ?? []).map((profile) =>
        profile._id === businessProfile._id ? businessProfile : profile,
      ),
    }));
    api.invalidateQueries(api.users.getProfiles, []);
  };

  useEffect(() => {
    api.challenges.get(
      challengeId,
      (challenge) => {
        setCreditsafeEnabled(!!challenge.creditsafeAvailable);
      },
      () => {
        setCreditsafeEnabled(false);
      },
    );
  }, [challengeId]);

  // Attach a new, or previously created organisation profile
  // Once done, update the idea locally and display the preview of the organisation profile
  const attachBusinessProfile = useCallback(
    (ideaBusinessProfile: OpenAPI.Schemas["IdeaBusinessProfile"]) => {
      updateIdea(ideaBusinessProfile._id, ["ideaBusinessProfile"]);
      updateIdea(ideaBusinessProfile, ["lockedOwnerIdeaBusinessProfile"], true);
    },
    [updateIdea],
  );

  const unattachBusinessProfile = useCallback(() => {
    updateIdea(null, ["ideaBusinessProfile"]);
    updateIdea(null, ["ideaBusinessProfileDetails"]);
    updateIdea(null, ["lockedOwnerIdeaBusinessProfile"], true);
  }, [updateIdea]);

  const viewReportForCompany = useCallback(() => {
    if (ideaBusinessProfile?._id) {
      setGeneratingCreditReport(true);
      api.users.generateIdeaBusinessProfileCreditReport(
        ideaBusinessProfile?._id,
        (response: CreditReportResponse) => {
          if (!response.success) {
            toast.error("Could not generate CreditSafe report");
            setGeneratingCreditReport(false);
            return;
          }
          // undefined if opened in idea preview, the update is only really for the frontend anyway
          // The actual caching is built into the API
          updateIdea?.(
            response.creditsafeReportUrl,
            ["lockedOwnerIdeaBusinessProfile", "creditsafeReportUrl"],
            true,
          );
          setGeneratingCreditReport(false);
          if (response.creditsafeReportUrl) {
            window.open(
              response.creditsafeReportUrl,
              "_blank",
              "noopener noreferrer",
            );
          }
        },
        (err) => {
          toast.error(err.message);
          setGeneratingCreditReport(false);
        },
      );
    }
  }, [ideaBusinessProfile?._id, updateIdea]);

  const createProfileMutation = useMutation({
    mutationFn: asMutation(api.users.createProfile)!,
    onSuccess: ({ ideaBusinessProfile }) => {
      toast.success("Profile created");
      dispatch(
        actions.user.updatePermissions(ideaBusinessProfile._id, [
          "ideaBusinessProfile.viewDetails",
          "ideaBusinessProfile.editDetails",
          "ideaBusinessProfile.viewMembers",
          "ideaBusinessProfile.editMembers",
          "ideaBusinessProfile.deleteProfile",
        ]),
      );
      onProfileUpdated(ideaBusinessProfile);
    },
    onError: () => {
      toast.error("Failed to create profile");
    },
  });

  const createBusinessProfile = useCallback(
    (profile: Partial<OpenAPI.Schemas["IdeaBusinessProfile"]>) => {
      createProfileMutation.mutate(
        [profile as OpenAPI.Schemas["IdeaBusinessProfile"]],
        {
          onSuccess: ({ ideaBusinessProfile }) => {
            setModalConfig({ openInEditMode: true, defaultTab: 0 });
            attachBusinessProfile(ideaBusinessProfile);
            setIsCreationModalOpen(false);
            setIsProfileModalOpen(true);
          },
        },
      );
    },
    [createProfileMutation, attachBusinessProfile],
  );

  const joinProfileMutation = useMutation({
    mutationKey: ["post", "users", "profiles", "join"],
    mutationFn: asMutation(api.users.joinProfile),
    onSuccess: () => {
      toast.success("Profile joined");
    },
    onError: () => {
      toast.error("Failed to join profile");
    },
  });

  const joinProfile = useCallback(
    (profileId: string) => {
      joinProfileMutation.mutate([profileId], {
        onSuccess: () => {
          // Invalidate the profiles query to refetch the updated list of profiles
          reloadUser();
          api.invalidateQueries(api.users.getProfiles, []);
        },
      });
    },
    [joinProfileMutation, reloadUser],
  );

  if (!isEditing && !ideaBusinessProfile) {
    return null;
  }

  const currentYear = new Date().getFullYear();

  const requiredInfoFields = [
    { label: "Name", value: name },
    {
      label: "Your business website",
      value: websiteUrl,
      skipped: websiteUrlSkipped,
    },
    { label: "Phone number", value: phoneNumber },
    { label: "Street address", value: streetAddress },
    { label: "Country", value: country },
    { label: "Region", value: region, skipped: country !== "GB" },
    { label: "City", value: city },
    { label: "Postcode", value: postcode },
    { label: "Organisation size", value: size },
    { label: "Organisation type", value: type },
    { label: "Organisation status", value: status },
    {
      label: "Organisation founding year",
      value:
        foundingYear && foundingYear > 999 && foundingYear <= currentYear
          ? foundingYear
          : "",
    },
    {
      label: "Organisation registration number",
      value: creditsafeRegNo,
      skipped: creditsafeRegNoSkipped,
    },
    { label: "VAT registration number", value: vatNo, skipped: vatNoSkipped },
  ];
  const isMissingRequiredInfo = requiredInfoFields.some(
    (field) => !field.value && !field.skipped,
  );

  const requiredContactFields = [
    { label: "Contact name", value: primaryContact?.fullName },
    { label: "Contact position or job title", value: primaryContact?.jobTitle },
    { label: "Contact phone number", value: primaryContact?.phoneNumber },
    { label: "Contact email", value: primaryContact?.email },
  ];
  const isMissingRequiredContactInfo = requiredContactFields.some(
    (field) => !field.value,
  );

  const requiredAddressFields = [
    { label: "Street address", value: tradingAddress?.streetAddress },
    { label: "City", value: tradingAddress?.city },
    { label: "Country", value: tradingAddress?.country },
    { label: "Postcode", value: tradingAddress?.postcode },
  ];
  const isMissingRequiredAddressInfo = requiredAddressFields.some(
    (field) => !field.value,
  );
  return (
    <IdeaSection
      style={style}
      ref={setFieldRef && ((ref) => setFieldRef(ref, "ideaBusinessProfile"))}
    >
      {isProfileModalOpen && ideaBusinessProfile ? (
        <IdeaBusinessProfileModal
          key="idea-business-profile-modal"
          config={modalConfig}
          businessProfile={ideaBusinessProfile}
          onClose={() => {
            setIsProfileModalOpen(false);
            updateIdea(ideaBusinessProfile._id, ["ideaBusinessProfile"]);
          }}
          onSave={(savedProfile) => {
            attachBusinessProfile(savedProfile);
          }}
          onDelete={() => {
            updateIdea(null, ["lockedOwnerIdeaBusinessProfile"], true);
            updateIdea(null, ["ideaBusinessProfile"]);
            setIsProfileModalOpen(false);
          }}
        />
      ) : null}
      {isCreationModalOpen ? (
        <BusinessProfileSearcher
          onClose={() => setIsCreationModalOpen(false)}
          onSelect={(profile) => createBusinessProfile(profile)}
          forType="idea"
          forId={idea._id}
        />
      ) : null}
      <H3>
        {isEditing || !ideaBusinessProfile?.name
          ? "Organisation details"
          : ideaBusinessProfile?.name}
        {isEditing &&
        idea.ownerIdeaTemplate?.body?.find(
          (section) => section.type === "businessProfile",
        )?.isRequired ? (
          <Popup
            size="small"
            trigger={
              <Icon
                icon={AsteriskIcon}
                style={{ position: "relative", top: -5, marginLeft: 3 }}
                color="red"
                size="small"
              />
            }
          >{`This field must be filled in to submit your ${t("generic.idea")}`}</Popup>
        ) : null}
      </H3>
      {canEditProfile && isEditing ? (
        <span className="title-explainer">
          Please provide your organisation&apos;s details to supplement your{" "}
          {t("generic.idea")}.
        </span>
      ) : (
        <div style={{ marginBottom: 5 }} />
      )}

      {isEditing && ideaBusinessProfile && isMissingRequiredInfo ? (
        canEditProfile ? (
          <Message color="orange" size="small">
            <Icon icon={TriangleAlertIcon} />
            <MessageHeader>
              Your organisation profile is missing required information
            </MessageHeader>
            <MessageContent>
              <p>
                The following parts of your organisation&apos;s profile are
                still missing or incomplete. Please make sure to complete them
                before submitting your {t("generic.idea")}.
              </p>
              <List variant="bulleted" compact>
                {requiredInfoFields.map((field) => {
                  if (field.value || field.skipped) {
                    return null;
                  }
                  return <ListItem key={field.label}>{field.label}</ListItem>;
                })}
              </List>
              <Button
                size="small"
                onClick={() => {
                  setModalConfig({ openInEditMode: true, defaultTab: 0 });
                  setIsProfileModalOpen(true);
                }}
                variant="outline"
              >
                <Icon icon={PencilIcon} />
                Edit profile
              </Button>
            </MessageContent>
          </Message>
        ) : (
          <Message color="orange">
            <Icon icon={TriangleAlertIcon} />
            <MessageHeader>
              Your organisation profile is missing required information
            </MessageHeader>
            <MessageContent>
              <p>
                The following parts of your organisation&apos;s profile are
                still missing or incomplete. They need to be completed before
                submitting your {t("generic.idea")}, however you do not have
                permission to edit the profile itself.
              </p>
              <List variant="bulleted" compact>
                {requiredInfoFields.map((field) => {
                  if (field.value || field.skipped) {
                    return null;
                  }
                  return <ListItem key={field.label}>{field.label}</ListItem>;
                })}
              </List>
              <p>
                The following users are members of the profile and have the
                necessary permissions to amend the required information.
              </p>
              <List variant="bulleted" compact>
                {ideaBusinessProfile?.ownerUsers?.map((profileUser) => (
                  <ListItem key={profileUser._id}>
                    <UserChip user={profileUser} compact="very" />
                  </ListItem>
                ))}
              </List>
            </MessageContent>
          </Message>
        )
      ) : null}

      {isEditing ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            alignItems: profilesQuery.data?.profiles ? "flex-end" : "stretch",
          }}
        >
          <BusinessProfileSelector
            key={ideaBusinessProfile?._id}
            selectedBusinessProfile={ideaBusinessProfile}
            isLoading={profilesQuery.isLoading}
            existingProfiles={profilesQuery.data?.profiles ?? []}
            joinableProfiles={profilesQuery.data?.joinableProfiles ?? []}
            selectBusinessProfile={(ideaBusinessProfile) =>
              attachBusinessProfile(ideaBusinessProfile)
            }
            joinProfile={(profileId) => joinProfile(profileId)}
            deselectBusinessProfile={unattachBusinessProfile}
          />
          {ideaBusinessProfile ? (
            canEditProfile ? (
              <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
                <Button
                  onClick={() => {
                    setModalConfig({ openInEditMode: false, defaultTab: 0 });
                    setIsProfileModalOpen(true);
                  }}
                  variant="outline"
                >
                  <Icon icon={PencilIcon} />
                  Manage profile
                </Button>
                <Button
                  onClick={() => {
                    unattachBusinessProfile();
                  }}
                  variant="outline"
                >
                  <Icon icon={Trash2Icon} />
                </Button>
              </div>
            ) : null
          ) : (
            <Button
              onClick={() => setIsCreationModalOpen(true)}
              color={
                profilesQuery.data?.profiles?.length === 0
                  ? "secondary"
                  : undefined
              }
              variant={
                profilesQuery.data?.profiles?.length !== 0
                  ? "outline"
                  : "regular"
              }
            >
              <Icon icon={PlusIcon} />
              Create new profile
            </Button>
          )}
        </div>
      ) : null}

      {ideaBusinessProfile ? (
        <div style={{ marginTop: 16 }}>
          <Overview businessProfile={ideaBusinessProfile} />

          <Grid columns={theme.sizes.isComputer ? 2 : 1}>
            <GridColumn>
              <H4>
                Primary contact
                {isEditing ? (
                  <Icon
                    icon={AsteriskIcon}
                    style={{ position: "relative", top: -5, marginLeft: 3 }}
                    color="red"
                    size="small"
                  />
                ) : null}
              </H4>
              {isEditing ? (
                <>
                  {ideaBusinessProfile?.contacts?.length ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {primaryContact ? (
                        canEditProfile && isMissingRequiredContactInfo ? (
                          <Message color="orange" size="small">
                            <Icon icon={TriangleAlertIcon} />
                            <MessageHeader>
                              Missing primary contact information
                            </MessageHeader>
                            <MessageContent>
                              <p>
                                The following parts of your primary contact
                                &apos;s detail sheet are missing or incomplete.
                              </p>
                              <List variant="bulleted" compact>
                                {requiredContactFields.map((field) => {
                                  if (field.value) {
                                    return null;
                                  }
                                  return (
                                    <ListItem key={field.label}>
                                      {field.label}
                                    </ListItem>
                                  );
                                })}
                              </List>
                              <Button
                                size="small"
                                onClick={() => {
                                  setModalConfig({
                                    openInEditMode: false,
                                    defaultTab: 1,
                                  });
                                  setIsProfileModalOpen(true);
                                }}
                                variant="outline"
                              >
                                <Icon icon={PencilIcon} />
                                Edit contact
                              </Button>
                            </MessageContent>
                          </Message>
                        ) : null
                      ) : (
                        <Message color="orange">
                          <MessageHeader>
                            No primary contact selected
                          </MessageHeader>
                          <p>
                            Choosing a primary contact is a requirement for
                            submission, please select a contact to continue.
                          </p>
                        </Message>
                      )}

                      <Dropdown
                        variant="outline"
                        clearable
                        fluid
                        placeholder="Select a primary contact.."
                        value={primaryContact?.id}
                        onChange={(_e, { value }) => {
                          updateIdea(value, [
                            "ideaBusinessProfileDetails",
                            "primaryContact",
                          ]);
                        }}
                        options={ideaBusinessProfile?.contacts?.map(
                          (contact) => ({
                            key: contact.id,
                            value: contact.id,
                            text: contact.fullName,
                          }),
                        )}
                      />
                      {canEditProfile ? (
                        <Button
                          style={{ alignSelf: "flex-end" }}
                          onClick={() => {
                            setIsProfileModalOpen(true);
                            setModalConfig({
                              openInEditMode: false,
                              defaultTab: 1,
                            });
                          }}
                          variant="outline"
                        >
                          <Icon icon={PencilIcon} />
                          Manage contacts
                        </Button>
                      ) : null}
                    </div>
                  ) : canEditProfile ? (
                    <Message color="orange">
                      <MessageHeader>No contacts found</MessageHeader>
                      <p>
                        Choosing a primary contact is a requirement for
                        submission, but this organisation profile has no
                        contacts set up yet. Please add at least one contact to
                        continue.
                      </p>
                      <Button
                        size="small"
                        onClick={() => {
                          setIsProfileModalOpen(true);
                          setModalConfig({
                            openInEditMode: false,
                            defaultTab: 1,
                          });
                        }}
                        variant="outline"
                      >
                        <Icon icon={PencilIcon} />
                        Create a contact
                      </Button>
                    </Message>
                  ) : null}
                </>
              ) : null}

              {primaryContact ? (
                <Card fluid placeholder>
                  <CardContent>
                    <ContactLayout contact={primaryContact} />
                  </CardContent>
                </Card>
              ) : null}
            </GridColumn>
            <GridColumn>
              <H4>
                Trading address
                {isEditing ? (
                  <Icon
                    icon={AsteriskIcon}
                    style={{ position: "relative", top: -5, marginLeft: 3 }}
                    color="red"
                    size="small"
                  />
                ) : null}
              </H4>
              {isEditing ? (
                <>
                  {ideaBusinessProfile?.addresses?.length ||
                  ideaBusinessProfile?.address ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {tradingAddress ? (
                        canEditProfile && isMissingRequiredAddressInfo ? (
                          <Message color="orange" size="small">
                            <Icon icon={TriangleAlertIcon} />
                            <MessageHeader>
                              Missing trading address information
                            </MessageHeader>
                            <MessageContent>
                              <p>
                                The following parts of your trading address
                                detail sheet are missing or incomplete.
                              </p>
                              <List variant="bulleted" compact>
                                {requiredAddressFields.map((field) => {
                                  if (field.value) {
                                    return null;
                                  }
                                  return (
                                    <ListItem key={field.label}>
                                      {field.label}
                                    </ListItem>
                                  );
                                })}
                              </List>
                              <Button
                                size="small"
                                onClick={() => {
                                  setModalConfig({
                                    openInEditMode: false,
                                    defaultTab: 1,
                                  });
                                  setIsProfileModalOpen(true);
                                }}
                                variant="outline"
                              >
                                <Icon icon={PencilIcon} />
                                Edit address
                              </Button>
                            </MessageContent>
                          </Message>
                        ) : null
                      ) : (
                        <Message color="orange">
                          <MessageHeader>
                            No trading address selected
                          </MessageHeader>
                          <p>
                            Choosing a trading address is a requirement for
                            submission, please select an address to continue.
                          </p>
                        </Message>
                      )}

                      <Dropdown
                        variant="outline"
                        clearable
                        fluid
                        placeholder="Select a trading address.."
                        value={tradingAddress?.id}
                        onChange={(_e, { value }) => {
                          updateIdea(value, [
                            "ideaBusinessProfileDetails",
                            "tradingAddress",
                          ]);
                        }}

                        options={[
                          {
                            key: "registeredAddress",
                            value: "registeredAddress",
                            text: "Registered address",
                          },
                          ...(ideaBusinessProfile?.addresses?.map(
                            (address) => ({
                              key: address?.id,
                              value: address.id,
                              text: address.streetAddress || "(Empty address)",
                            }),
                          ) || []),
                        ]}
                      />
                      {canEditProfile ? (
                        <Button
                          style={{ alignSelf: "flex-end" }}
                          onClick={() => {
                            setIsProfileModalOpen(true);
                            setModalConfig({
                              openInEditMode: false,
                              defaultTab: 1,
                            });
                          }}
                          variant="outline"
                        >
                          <Icon icon={PencilIcon} />
                          Manage addresses
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <Message color="orange">
                      <MessageHeader>No addresses found</MessageHeader>
                      <p>
                        Choosing a trading address is a requirement for
                        submission, but this organisation profile has no
                        addresses set up yet. Please add at least one address to
                        continue.
                      </p>
                      <Button
                        size="small"
                        onClick={() => {
                          setIsProfileModalOpen(true);
                          setModalConfig({
                            openInEditMode: false,
                            defaultTab: 1,
                          });
                        }}
                        variant="outline"
                      >
                        <Icon icon={PencilIcon} />
                        Create an address
                      </Button>
                    </Message>
                  )}
                </>
              ) : null}
              {tradingAddress ? (
                <Card fluid placeholder>
                  <CardContent>
                    <AddressLayout address={tradingAddress} />
                  </CardContent>
                </Card>
              ) : null}
            </GridColumn>
          </Grid>

          <Divider hidden />
          {creditsafeEnabled && creditsafeCompanyId && hasReportPermissions ? (
            <div className="credit-report-area">
              {creditsafeReportUrl ? (
                <ButtonLink
                  to={creditsafeReportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon icon={FileTextIcon} />
                  Download credit report
                </ButtonLink>
              ) : creditsafeEnabled ? (
                <Button
                  onClick={() => viewReportForCompany()}
                  loading={generatingCreditReport}
                >
                  <Icon icon={FileTextIcon} />
                  Download credit report
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </IdeaSection>
  );
};

export default IdeaBusinessProfileComponent;
